"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { scheduleIdle } from "@/lib/analytics/schedule-idle";

import { loadMobileNav, type MobileNavComponent } from "./mobile-nav-loader";
import { NAV_MENU_BUTTON_MEDIA_QUERY } from "./Nav.constants";

type MobileNavState = {
  /** The menu sheet, once its code has loaded. */
  MobileNav: MobileNavComponent | null;
  /** True when the visitor asked for the menu and its code failed to load. */
  failed: boolean;
  /** Start loading. `visible` means the visitor asked for it, so a failure shows. */
  request: (visible: boolean) => void;
};

/**
 * Loads the mobile menu sheet on demand: on idle after first load on small
 * screens (so it is cached before going offline), on hover or focus of the
 * menu button, and on tap. Concurrent requests share one load. A failed load
 * never throws; the next request tries again.
 */
export const useMobileNav = (): MobileNavState => {
  const [MobileNav, setMobileNav] = useState<MobileNavComponent | null>(null);
  const [failed, setFailed] = useState(false);
  const inFlightRef = useRef<Promise<MobileNavComponent> | null>(null);
  const visibleRef = useRef(false);

  const request = useCallback((visible: boolean) => {
    if (visible) {
      visibleRef.current = true;
      setFailed(false);
    }
    if (inFlightRef.current) return;

    const load = loadMobileNav();
    inFlightRef.current = load;
    load.then(
      (component) => {
        setMobileNav(() => component);
        setFailed(false);
      },
      () => {
        inFlightRef.current = null;
        if (visibleRef.current) setFailed(true);
        visibleRef.current = false;
      },
    );
  }, []);

  useEffect(() => {
    if (!window.matchMedia(NAV_MENU_BUTTON_MEDIA_QUERY).matches) return;

    let active = true;
    const preload = () =>
      scheduleIdle(() => {
        if (active) request(false);
      });

    if (document.readyState === "complete") {
      preload();
    } else {
      window.addEventListener("load", preload, { once: true });
    }
    return () => {
      active = false;
      window.removeEventListener("load", preload);
    };
  }, [request]);

  return { MobileNav, failed, request };
};
